import { api, useAuth } from '@shop/api-client'
import type { Warehouse } from '@shop/api-client'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

/**
 * محطّةُ العمل: <b>أيُّ فرعٍ وأيُّ مستودعٍ يبيع منه هذا الصندوق</b>.
 *
 * والفرعُ يأتي من الجلسة (<c>activeBranchId</c>)، والمستودعُ يُقرأ من دليل
 * التنظيم — <b>ولا يُكتب ثابتًا ولا يُخزَّن في المتصفّح</b>: منحُ مستودعٍ
 * يُسحب من الموظّف فيسري في الطلب التالي، ونسخةٌ محفوظةٌ محلّيًّا تبقى تبيع
 * من مستودعٍ لم يعد له.
 *
 * ⚠ <b>ولا يُختار المستودعُ تلقائيًّا حين يكون له أكثرُ من واحد</b>: البيعُ من
 *   «التالف» بدل «الرئيسيّ» خطأٌ لا يُكتشف إلا بجرد. فيُعرض الاختيارُ
 *   للكاشير، ويُحسم بضغطةٍ واحدةٍ في أوّل اليوم.
 */
export type Station = {
  branchId: string | null
  warehouses: Warehouse[]
  warehouseId: string | null
  choose: (id: string) => void
  loading: boolean
  error: unknown
}

export function useStation(): Station {
  const { session } = useAuth()
  const branchId = session?.me?.activeBranchId ?? null

  const [chosen, setChosen] = useState<string | null>(null)

  const warehouses = useQuery({
    queryKey: ['warehouses', branchId],
    queryFn: () => api<Warehouse[]>(`/api/v1/warehouses?branchId=${branchId}`),
    enabled: branchId !== null,

    // المستودعاتُ لا تتغيّر في أثناء الورديّة — فلا تُسأل مع كلّ رسمة.
    staleTime: 5 * 60_000,
  })

  // ومستودعُ العبور ليس مكانَ بيع: النظامُ يمرّ به ولا يُباع منه.
  const sellable = (warehouses.data ?? []).filter((w) => w.isActive && w.kind !== 'TRANSIT')

  return {
    branchId,
    warehouses: sellable,

    // واحدٌ ⇒ يُختار بلا سؤال. وأكثرُ ⇒ يقرّر الكاشير، واختيارُه يعلو.
    warehouseId: chosen ?? (sellable.length === 1 ? sellable[0].id : null),
    choose: setChosen,
    loading: branchId !== null && warehouses.isPending,
    error: warehouses.error,
  }
}
